import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { nuevoId } from '../../../lib/formularios';
import { useEditor } from './contexto';
import { GlifoOpcion } from './controles';

/** Filas y columnas de las cuadrículas (opción múltiple y casillas), lado a lado como en Google. */
export default function EditorCuadricula({ item }) {
  return (
    <div className="fe-cuadricula-editor">
      <ListaEjes item={item} eje="filas" rotulo="Filas" prefijo="Fila" />
      <ListaEjes item={item} eje="columnas" rotulo="Columnas" prefijo="Columna" />
    </div>
  );
}

function ListaEjes({ item, eje, rotulo, prefijo }) {
  const { cambiarItem } = useEditor();
  const lista = useMemo(() => item[eje] || [], [item, eje]);
  const refs = useRef({});
  const [enfocar, setEnfocar] = useState(null);

  useEffect(() => {
    if (!enfocar) return;
    refs.current[enfocar]?.focus();
    refs.current[enfocar]?.select();
    setEnfocar(null);
  }, [enfocar, lista]);

  const guardar = (nueva, clave) => {
    const campos = { [eje]: nueva };
    // Quitar una fila o columna también la quita de la clave de respuestas del cuestionario
    const correctas = item.clave?.correctas;
    if (correctas && typeof correctas === 'object' && !Array.isArray(correctas)) {
      const ids = new Set(nueva.map((x) => x.id));
      const limpias = eje === 'filas'
        ? Object.fromEntries(Object.entries(correctas).filter(([fila]) => ids.has(fila)))
        : Object.fromEntries(Object.entries(correctas).map(([fila, cols]) => [fila, (cols || []).filter((c) => ids.has(c))]));
      campos.clave = { ...item.clave, correctas: limpias };
    }
    cambiarItem(item.id, campos, { clave });
  };

  const agregar = (despuesDe = null, textos = null) => {
    const nueva = [...lista];
    const i = despuesDe ? nueva.findIndex((x) => x.id === despuesDe) + 1 : nueva.length;
    const elementos = (textos || [`${prefijo} ${lista.length + 1}`]).map((texto) => ({ id: nuevoId(), texto }));
    nueva.splice(i, 0, ...elementos);
    guardar(nueva);
    setEnfocar(elementos[elementos.length - 1].id);
  };

  return (
    <div className="fe-eje">
      <div className="fe-eje-rotulo">{rotulo}</div>
      {lista.map((x, i) => (
        <div key={x.id} className="fe-opcion-fila">
          {eje === 'filas'
            ? <span className="fe-glifo fe-glifo-numero">{i + 1}.</span>
            : <GlifoOpcion tipo={item.tipo === 'cuadricula_casillas' ? 'casillas' : 'opcion_multiple'} indice={i} />}
          <input
            ref={(el) => { refs.current[x.id] = el; }}
            className="fm-campo-linea fe-campo-opcion"
            value={x.texto}
            aria-label={`${prefijo} ${i + 1}`}
            onChange={(e) => guardar(lista.map((y) => (y.id === x.id ? { ...y, texto: e.target.value } : y)), `${item.id}:${eje}:${x.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                agregar(x.id);
              }
            }}
            onPaste={(e) => {
              const lineas = e.clipboardData.getData('text').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
              if (lineas.length < 2) return;
              e.preventDefault();
              const nueva = lista.map((y) => (y.id === x.id ? { ...y, texto: lineas[0] } : y));
              const extra = lineas.slice(1).map((texto) => ({ id: nuevoId(), texto }));
              nueva.splice(i + 1, 0, ...extra);
              guardar(nueva);
            }}
          />
          {lista.length > 1 && (
            <button
              type="button"
              className="fe-icono-btn"
              onClick={() => guardar(lista.filter((y) => y.id !== x.id))}
              aria-label={`Quitar ${x.texto}`}
              title="Quitar"
            >
              <X />
            </button>
          )}
        </div>
      ))}
      <div className="fe-opcion-fila fe-opcion-nueva">
        {eje === 'filas'
          ? <span className="fe-glifo fe-glifo-numero">{lista.length + 1}.</span>
          : <GlifoOpcion tipo={item.tipo === 'cuadricula_casillas' ? 'casillas' : 'opcion_multiple'} indice={lista.length} />}
        <button type="button" className="fe-agregar-opcion" onClick={() => agregar()}>
          Añadir {prefijo.toLowerCase()}
        </button>
      </div>
    </div>
  );
}
